import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Activity, Volume2, VolumeX, ServerCog, Database, HeartPulse,
  Upload, Trash2, Bell, Music,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const KEYS = {
  sound:     'manha:ecg:sound',
  volume:    'manha:ecg:volume',
  loopMp3:   'manha:ecg:loop_mp3',   // dataURL — user's uploaded heartbeat MP3
  alertMp3:  'manha:ecg:alert_mp3',  // dataURL — user's uploaded alert MP3
  loopName:  'manha:ecg:loop_name',
  alertName: 'manha:ecg:alert_name',
};
const MAX_MP3_BYTES = 3 * 1024 * 1024;   // 3 MB — safe for localStorage

// A single ECG wave (baseline → P → QRS → T → baseline). x runs 0..800.
function buildEcgPath(offset = 0) {
  const pts = [];
  const w = 800, h = 120, mid = h / 2;
  for (let x = 0; x <= w; x += 4) {
    const t = ((x + offset) % w) / w;
    let y = mid;
    if (t > 0.10 && t < 0.14) y = mid - 6;
    else if (t > 0.18 && t < 0.20) y = mid + 4;
    else if (t > 0.20 && t < 0.22) y = mid - 44;
    else if (t > 0.22 && t < 0.24) y = mid + 22;
    else if (t > 0.30 && t < 0.38) y = mid - 10;
    pts.push(`${x},${y.toFixed(1)}`);
  }
  return `M ${pts.join(' L ')}`;
}

function loadLS(k, dflt = null) {
  try { return localStorage.getItem(k) ?? dflt; } catch { return dflt; }
}
function saveLS(k, v) { try { localStorage.setItem(k, v); } catch { /* quota */ } }

export default function EcgHeartbeat({ testIdPrefix = 'deo' }) {
  const [status, setStatus] = useState({ api: 'up', mongo: 'up', mongo_latency_ms: 0, bpm: 60 });
  const [vitals, setVitals] = useState({ paid: 0, due: 0, overdue: 0 });
  const [soundOn, setSoundOn] = useState(loadLS(KEYS.sound) === '1');
  const [volume, setVolume]   = useState(Number(loadLS(KEYS.volume) ?? 0.6));
  const [loopMp3, setLoopMp3]   = useState(loadLS(KEYS.loopMp3));
  const [alertMp3, setAlertMp3] = useState(loadLS(KEYS.alertMp3));
  const [loopName, setLoopName]   = useState(loadLS(KEYS.loopName)  || '');
  const [alertName, setAlertName] = useState(loadLS(KEYS.alertName) || '');
  const [offset, setOffset] = useState(0);
  const [alertActive, setAlertActive] = useState(null); // { message } | null

  const audioCtxRef  = useRef(null);
  const loopAudioRef = useRef(null);   // HTMLAudioElement for custom loop
  const alertAudioRef= useRef(null);   // HTMLAudioElement for custom alert
  const beatTimerRef = useRef(null);   // synthesised beep interval
  const loopInFileRef  = useRef(null);
  const alertInFileRef = useRef(null);

  // ------------------------------------------------- pull admin-uploaded MP3s
  // Server-side ECG sounds (managed by Admin in Settings) trump any per-user
  // localStorage upload. Fetched once at mount — a settings tick can force a
  // re-fetch by dispatching `manha:ecg-sound-updated`.
  useEffect(() => {
    let cancel = false;
    const pull = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/settings/ecg-sound`,
          { withCredentials: true, timeout: 5000 });
        if (cancel) return;
        if (data?.loop?.data_url) {
          setLoopMp3(data.loop.data_url);
          setLoopName(data.loop.name || 'admin-loop.mp3');
        }
        if (data?.alert?.data_url) {
          setAlertMp3(data.alert.data_url);
          setAlertName(data.alert.name || 'admin-alert.mp3');
        }
      } catch { /* fall back to any localStorage upload */ }
    };
    pull();
    const onUpdate = () => pull();
    window.addEventListener('manha:ecg-sound-updated', onUpdate);
    return () => { cancel = true; window.removeEventListener('manha:ecg-sound-updated', onUpdate); };
  }, []);

  // ------------------------------------------------- poll system status
  // Slowed to 10 s (was 3.5 s) and paused while the tab is hidden — the
  // difference is >90 % less background CPU for tabs the user isn't looking
  // at, with no visible impact on freshness.
  useEffect(() => {
    let cancel = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/dashboard/system-status`,
          { withCredentials: true, timeout: 4000 });
        if (!cancel) setStatus(data);
      } catch {
        if (!cancel) setStatus(s => ({ ...s, api: 'down' }));
      }
    };
    tick();
    const iv = setInterval(tick, 10000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancel = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // ------------------------------------------------- poll installment vitals
  // 60 s cadence (was 15 s) — the backend already caches this for 20 s so a
  // faster poll would only hit the cache anyway. Paused while tab is hidden.
  useEffect(() => {
    let cancel = false;
    const tick = async () => {
      if (document.hidden) return;
      try {
        const { data } = await axios.get(`${API_URL}/api/reports/installments/summary`,
          { withCredentials: true, timeout: 6000 });
        if (!cancel) setVitals({
          paid:    data?.paid?.count ?? 0,
          due:     (data?.upcoming?.count ?? 0) + (data?.due_today?.count ?? 0),
          overdue: data?.overdue?.count ?? 0,
        });
      } catch { /* keep last snapshot */ }
    };
    tick();
    const iv = setInterval(tick, 60000);
    const onVis = () => { if (!document.hidden) tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => { cancel = true; clearInterval(iv); document.removeEventListener('visibilitychange', onVis); };
  }, []);

  // ------------------------------------------------- animate 3 ECG lines
  // rAF halts when the tab is hidden (browser does this automatically for
  // background tabs, but we also stop it explicitly so the state updater
  // doesn't queue up work either).
  useEffect(() => {
    let raf;
    let last = performance.now();
    let running = !document.hidden;
    const step = (now) => {
      if (!running) return;
      const dt = now - last; last = now;
      const base = (status.bpm || 60) / 60 * (800 / 1000);
      setOffset(o => (o + base * dt) % 800);
      raf = requestAnimationFrame(step);
    };
    const onVis = () => {
      running = !document.hidden;
      if (running) { last = performance.now(); raf = requestAnimationFrame(step); }
    };
    document.addEventListener('visibilitychange', onVis);
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); document.removeEventListener('visibilitychange', onVis); };
  }, [status.bpm]);

  // ------------------------------------------------- ECG audio engine
  // Priority: custom uploaded loop MP3 > synthesised WebAudio beep.
  const cleanupEcgAudio = () => {
    if (beatTimerRef.current) { clearInterval(beatTimerRef.current); beatTimerRef.current = null; }
    if (loopAudioRef.current) {
      try { loopAudioRef.current.pause(); } catch {}
      loopAudioRef.current = null;
    }
  };

  useEffect(() => {
    cleanupEcgAudio();
    saveLS(KEYS.sound, soundOn ? '1' : '0');
    if (!soundOn) return;

    // ---- Option A — user uploaded a custom MP3 loop
    if (loopMp3) {
      const a = new Audio(loopMp3);
      a.loop = true;
      a.volume = volume;
      a.play().catch(() => { /* browser may block until user gesture */ });
      loopAudioRef.current = a;
      return;
    }

    // ---- Option B — synthesised beep at BPM
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
      catch { return; }
    }
    const beep = () => {
      const ctx = audioCtxRef.current; if (!ctx) return;
      const t = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, t);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(0.15 * volume, t + 0.005);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + 0.12);
      osc.connect(gain).connect(ctx.destination);
      osc.start(t); osc.stop(t + 0.15);
    };
    beep();
    beatTimerRef.current = setInterval(beep, 60000 / (status.bpm || 60));
    return () => cleanupEcgAudio();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [soundOn, loopMp3, status.bpm]);

  // Volume follows without restarting the loop
  useEffect(() => {
    saveLS(KEYS.volume, String(volume));
    if (loopAudioRef.current) loopAudioRef.current.volume = alertActive ? volume * 0.15 : volume;
  }, [volume, alertActive]);

  // ------------------------------------------------- critical alert bus
  // Any part of the app can dispatch:
  //   window.dispatchEvent(new CustomEvent('manha:critical-alert',
  //       { detail: { message: 'Intrusion detected', mp3Url?: '...' } }));
  useEffect(() => {
    const onAlert = async (e) => {
      const detail = (e && e.detail) || {};
      setAlertActive({ message: detail.message || 'Critical alert' });
      // Duck the ECG loop
      if (loopAudioRef.current) loopAudioRef.current.volume = volume * 0.15;

      const alertSrc = detail.mp3Url || alertMp3;
      if (alertSrc) {
        const a = new Audio(alertSrc);
        a.volume = Math.min(1, volume * 1.5);
        alertAudioRef.current = a;
        a.addEventListener('ended', () => restoreEcgAudio());
        try { await a.play(); }
        catch { toast.warning('Browser blocked alert audio — click anywhere and retry'); restoreEcgAudio(); }
      } else {
        // Built-in synthesised alert — three fast high-pitched beeps
        playBuiltinAlert(volume, () => restoreEcgAudio());
      }

      // Safety timeout in case ended never fires
      setTimeout(() => { if (alertAudioRef.current) restoreEcgAudio(); }, 8000);
    };
    window.addEventListener('manha:critical-alert', onAlert);
    return () => window.removeEventListener('manha:critical-alert', onAlert);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volume, alertMp3, soundOn]);

  const restoreEcgAudio = () => {
    if (alertAudioRef.current) {
      try { alertAudioRef.current.pause(); } catch {}
      alertAudioRef.current = null;
    }
    if (loopAudioRef.current) loopAudioRef.current.volume = volume;
    setAlertActive(null);
  };

  const playBuiltinAlert = (vol, onDone) => {
    if (!audioCtxRef.current) {
      try { audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)(); }
      catch { onDone && onDone(); return; }
    }
    const ctx = audioCtxRef.current;
    const at = ctx.currentTime;
    const gains = [1600, 1600, 2000]; // Hz per pulse
    gains.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      const t0 = at + i * 0.35;
      osc.type = 'square';
      osc.frequency.setValueAtTime(freq, t0);
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.45 * vol, t0 + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.30);
      osc.connect(g).connect(ctx.destination);
      osc.start(t0); osc.stop(t0 + 0.32);
    });
    setTimeout(() => onDone && onDone(), gains.length * 350 + 100);
  };

  // ------------------------------------------------- MP3 uploads
  const handleUpload = async (kind, file) => {
    if (!file) return;
    if (!file.type.startsWith('audio')) {
      toast.error('Please upload an audio file (MP3, WAV, OGG).');
      return;
    }
    if (file.size > MAX_MP3_BYTES) {
      toast.error(`File too large — max ${(MAX_MP3_BYTES / 1024 / 1024).toFixed(0)} MB`);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = String(reader.result);
      try {
        if (kind === 'loop') {
          saveLS(KEYS.loopMp3, dataUrl);
          saveLS(KEYS.loopName, file.name);
          setLoopMp3(dataUrl); setLoopName(file.name);
          toast.success(`Heartbeat sound set — ${file.name}`);
        } else {
          saveLS(KEYS.alertMp3, dataUrl);
          saveLS(KEYS.alertName, file.name);
          setAlertMp3(dataUrl); setAlertName(file.name);
          toast.success(`Alert sound set — ${file.name}`);
        }
      } catch {
        toast.error('Could not save — try a smaller file');
      }
    };
    reader.onerror = () => toast.error('Failed to read file');
    reader.readAsDataURL(file);
  };

  const clearUpload = (kind) => {
    if (kind === 'loop')  { localStorage.removeItem(KEYS.loopMp3);  localStorage.removeItem(KEYS.loopName);  setLoopMp3(null);  setLoopName(''); }
    else                  { localStorage.removeItem(KEYS.alertMp3); localStorage.removeItem(KEYS.alertName); setAlertMp3(null); setAlertName(''); }
    toast.info(`${kind === 'loop' ? 'Heartbeat' : 'Alert'} sound cleared`);
  };

  const testAlert = () => {
    window.dispatchEvent(new CustomEvent('manha:critical-alert',
      { detail: { message: 'Test alert — this is how a real alert will sound' } }));
  };

  const apiOk = status.api === 'up';
  const dbOk  = status.mongo === 'up';

  // 3 lanes — Paid (green), Due/Upcoming (amber), Overdue (rose).
  // Each lane's beat speed scales with how many items it holds so the panel
  // visually communicates load (calm when 0, faster when many).
  const lanes = [
    { key: 'paid',    label: 'Normal · Paid',     color: '#10b981', // emerald
      count: vitals.paid,    speedMul: Math.min(1.6, 0.9 + Math.log10(1 + vitals.paid)   * 0.35) },
    { key: 'due',     label: 'Due · Upcoming',    color: '#f59e0b', // amber
      count: vitals.due,     speedMul: Math.min(1.7, 0.9 + Math.log10(1 + vitals.due)    * 0.4)  },
    { key: 'overdue', label: 'Overdue',           color: '#f43f5e', // rose
      count: vitals.overdue, speedMul: Math.min(2.0, 1.0 + Math.log10(1 + vitals.overdue) * 0.55) },
  ];

  return (
    <Card className={`bg-slate-900 border-slate-800 overflow-hidden transition-shadow
                       ${alertActive ? 'ring-2 ring-rose-500 shadow-[0_0_30px_rgba(244,63,94,0.5)]' : ''}`}
      data-testid={`${testIdPrefix}-ecg-card`}>
      <CardContent className="p-4">
        {/* -------- Header row -------- */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <HeartPulse className={`h-5 w-5 ${alertActive ? 'text-rose-500' : 'text-rose-400'} animate-pulse`} />
              <h3 className="text-white font-semibold">System Vitals</h3>
              <span className="text-xs text-slate-500">Live</span>
              {alertActive && (
                <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full
                                  bg-rose-500/20 text-rose-300 border border-rose-500/50 text-xs animate-pulse"
                  data-testid={`${testIdPrefix}-alert-banner`}>
                  <Bell className="h-3 w-3" /> ALERT · {alertActive.message}
                </span>
              )}
            </div>
            <div className="flex gap-4 mt-2 flex-wrap">
              <StatusPill icon={ServerCog} label="API" ok={apiOk}
                testId={`${testIdPrefix}-status-api`} />
              <StatusPill icon={Database} label="MongoDB" ok={dbOk}
                sub={`${status.mongo_latency_ms}ms`} testId={`${testIdPrefix}-status-db`} />
              <StatusPill icon={Activity} label="Heart Rate" ok={apiOk && dbOk}
                sub={`${status.bpm || 60} bpm`} testId={`${testIdPrefix}-status-bpm`} />
            </div>
          </div>

          {/* Sound controls */}
          <div className="flex flex-col gap-2 min-w-[220px]">
            <div className="flex items-center gap-2 justify-end">
              {soundOn ? <Volume2 className="h-4 w-4 text-emerald-400" /> : <VolumeX className="h-4 w-4 text-slate-500" />}
              <Label htmlFor="ecg-sound" className="text-slate-300 text-sm">Beep</Label>
              <Switch id="ecg-sound" checked={soundOn} onCheckedChange={setSoundOn}
                data-testid={`${testIdPrefix}-ecg-sound-toggle`} />
            </div>
            <div className="flex items-center gap-2">
              <VolumeX className="h-3.5 w-3.5 text-slate-500" />
              <Slider min={0} max={100} step={1}
                value={[Math.round(volume * 100)]}
                onValueChange={(v) => setVolume((v[0] || 0) / 100)}
                className="flex-1"
                data-testid={`${testIdPrefix}-ecg-volume`} />
              <Volume2 className="h-3.5 w-3.5 text-slate-400" />
              <span className="text-xs text-slate-400 w-8 text-right">{Math.round(volume * 100)}%</span>
            </div>
          </div>
        </div>

        {/* -------- 3-lane ECG monitor -------- */}
        <div className={`mt-4 rounded-sm bg-slate-950 border overflow-hidden transition-colors
                          ${alertActive ? 'border-rose-500/60' : 'border-slate-800'}`}>
          {lanes.map((lane, idx) => (
            <EcgLane
              key={lane.key}
              lane={lane}
              offset={offset * lane.speedMul}
              alertActive={!!alertActive}
              testId={`${testIdPrefix}-ecg-lane-${lane.key}`}
              first={idx === 0}
            />
          ))}
        </div>

        {/* Custom sound uploads are Admin-only via Company Settings —
             the DEO panel just consumes the admin-uploaded MP3s. */}

        {/* -------- Test alert button -------- */}
        <div className="mt-3 flex items-center justify-end">
          <Button onClick={testAlert} variant="outline"
            className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10"
            data-testid={`${testIdPrefix}-ecg-test-alert`}>
            <Bell className="h-4 w-4 mr-2" />
            Test Alert
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ icon: Icon, label, ok, sub, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Icon className={`h-4 w-4 ${ok ? 'text-emerald-400' : 'text-rose-400'}`} />
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
        <div className={`text-sm font-semibold ${ok ? 'text-emerald-300' : 'text-rose-300'}`}>
          {ok ? 'Healthy' : 'Down'} {sub && <span className="text-slate-500 font-normal ml-1">· {sub}</span>}
        </div>
      </div>
    </div>
  );
}

function SoundRow({ label, hint, icon: Icon, fileName, onPick, onClear, testId }) {
  return (
    <div className="rounded-sm border border-slate-800 bg-slate-900/60 p-3 flex items-center justify-between gap-3"
      data-testid={testId}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 text-white text-sm">
          <Icon className="h-4 w-4 text-slate-400" /> {label}
        </div>
        <div className="text-xs text-slate-500 mt-0.5 truncate">
          {fileName ? <span className="text-emerald-400">Loaded: {fileName}</span> : hint}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Button variant="outline" size="sm" onClick={onPick}
          className="border-slate-700 text-slate-200"
          data-testid={`${testId}-upload`}>
          <Upload className="h-4 w-4 mr-1.5" /> Upload
        </Button>
        {onClear && (
          <Button variant="ghost" size="sm" onClick={onClear}
            className="text-rose-300 hover:bg-rose-500/10"
            data-testid={`${testId}-clear`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
}


// ------------------------------------------------------------- EcgLane
// One horizontal ECG track. Colour + speed multiplier come from the parent
// so each lane (Paid / Due / Overdue) beats independently.
function EcgLane({ lane, offset, alertActive, testId, first }) {
  // In alert state, only the overdue lane goes rose-red glow-bright; the
  // other two dim slightly to draw the eye to the critical one.
  const dim = alertActive && lane.key !== 'overdue';
  const stroke = lane.color;
  const path = buildEcgPath(-offset);

  return (
    <div className={`flex items-stretch ${first ? '' : 'border-t border-slate-800/70'}`}
      data-testid={testId}>
      {/* Left label column */}
      <div className="w-40 min-w-[10rem] px-3 py-2 flex flex-col justify-center gap-0.5
                       bg-slate-900/60 border-r border-slate-800/70">
        <div className="flex items-center gap-2">
          <span className="inline-block h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: stroke, boxShadow: `0 0 8px ${stroke}` }} />
          <span className="text-xs uppercase tracking-wider text-slate-300 font-semibold">
            {lane.label}
          </span>
        </div>
        <div className="text-lg font-bold text-white leading-none" data-testid={`${testId}-count`}>
          {lane.count.toLocaleString()}
        </div>
      </div>

      {/* Wave column */}
      <div className="flex-1 relative bg-slate-950">
        <svg viewBox="0 0 800 120" preserveAspectRatio="none"
          className="w-full h-16"
          data-testid={`${testId}-svg`}>
          <defs>
            <pattern id={`grid-${lane.key}`} width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#1e293b" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="800" height="120" fill={`url(#grid-${lane.key})`} />
          <path d={path} fill="none" stroke={stroke} strokeWidth="2.4"
            strokeLinecap="round" strokeLinejoin="round"
            opacity={dim ? 0.35 : 1}
            style={{ filter: `drop-shadow(0 0 ${dim ? 2 : 5}px ${stroke})` }} />
        </svg>
        {/* Right-edge count badge — a live blip */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1
                         text-xs font-mono px-2 py-0.5 rounded-full bg-slate-900/70 border"
          style={{ borderColor: stroke + '60', color: stroke }}>
          <span className="h-1.5 w-1.5 rounded-full animate-pulse"
            style={{ backgroundColor: stroke, boxShadow: `0 0 6px ${stroke}` }} />
          {lane.count}
        </div>
      </div>
    </div>
  );
}
